import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const LS_KEY = 'crm_sidebar_collapsed';

const Sidebar: React.FC = () => {
  const { user, can } = useAuth();
  // external_user is a read-only role that should only see Leads.
  // Everything else in the sidebar hides so it can't wander into
  // pages that will 403 the moment they load.
  const isExternal = user?.role === 'external_user';

  // Collapsed state — persisted to localStorage so it survives navigation
  // and reload. The class is applied to <body> so a single CSS rule
  // (body.sidebar-collapsed) drives both the sidebar width AND main-wrap
  // margin-left via the --sidebar-w variable.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_KEY) === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    try { localStorage.setItem(LS_KEY, collapsed ? '1' : '0'); } catch {}
    return () => { document.body.classList.remove('sidebar-collapsed'); };
  }, [collapsed]);

  // Toggle from outside — Topbar hamburger fires this window event so the
  // collapse control can live in a part of the UI that isn't fighting the
  // sidebar's own scroll container for pixels.
  useEffect(() => {
    const handler = () => setCollapsed(c => !c);
    window.addEventListener('sidebar-toggle', handler);
    return () => window.removeEventListener('sidebar-toggle', handler);
  }, []);

  // Populate a native browser tooltip (title attr) from each NavLink's text
  // content so operators can identify icons when the sidebar is collapsed.
  // Runs once per collapse toggle — cheap because the DOM is stable.
  useEffect(() => {
    document.querySelectorAll<HTMLAnchorElement>('.sidebar-nav a').forEach(a => {
      const label = (a.textContent || '').trim();
      if (label && !a.getAttribute('title')) a.setAttribute('title', label);
    });
  }, [collapsed]);

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <div className="sidebar-brand-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
          </svg>
        </div>
        <span className="sidebar-brand-name">Auto<span>mately</span></span>
      </div>

      <nav className="sidebar-nav">
        {!isExternal && (
          <>
            <span className="nav-section-label">Overview</span>
            <NavLink exact to="/dashboard" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
              </svg>
              Dashboard
            </NavLink>
          </>
        )}

        <span className="nav-section-label" style={{ marginTop: 8 }}>Sales</span>

        {can('leads', 'view') && (
        <NavLink to="/leads" activeClassName="active" exact>
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4"/><path d="M6 20v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
          </svg>
          Leads
        </NavLink>
        )}

        {can('leads', 'view') && (
        <NavLink to="/leads/relationship-proposals" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/>
          </svg>
          Relationship Review
        </NavLink>
        )}

        {can('contacts', 'view') && (
        <NavLink to="/contacts" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
          </svg>
          Contacts
        </NavLink>
        )}

        {can('organizations', 'view') && (
        <NavLink to="/organizations" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2"/>
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
            <line x1="12" y1="12" x2="12" y2="16"/>
            <line x1="10" y1="14" x2="14" y2="14"/>
          </svg>
          Organizations
        </NavLink>
        )}

        {can('deals', 'view') && (
        <NavLink to="/deals" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          Deals
        </NavLink>
        )}

        {can('campaigns', 'view') && (
        <NavLink to="/campaigns" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/>
          </svg>
          Campaigns
        </NavLink>
        )}

        <span className="nav-section-label" style={{ marginTop: 8 }}>Work</span>

        {can('tasks', 'view') && (
        <NavLink to="/tasks" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 11 12 14 22 4"/>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
          </svg>
          Tasks
        </NavLink>
        )}

        {can('tasks', 'view') && (
        <NavLink to="/complaints" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          Complaints
        </NavLink>
        )}

        {/* Attendance — visible to all authenticated users EXCEPT external_user.
            Agents see their own calendar + check-in/out; managers/admins get a
            picker to view any agent's history. */}
        {!isExternal && (
          <NavLink to="/attendance" activeClassName="active">
            <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Attendance
          </NavLink>
        )}

        {!isExternal && (
          <NavLink to="/customers" activeClassName="active">
            <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Customers
          </NavLink>
        )}

        <span className="nav-section-label" style={{ marginTop: 8 }}>Telephony</span>

        {can('sim_calls', 'view') && (
        <NavLink to="/live-monitor" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="2"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
          </svg>
          Live Monitor
        </NavLink>
        )}

        {can('sim_calls', 'view') && (
        <NavLink to="/call-reports" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
          </svg>
          Call Reports
        </NavLink>
        )}

        {can('sim_calls', 'view') && (
        <NavLink to="/sim-calls" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
          </svg>
          SIM Calls
        </NavLink>
        )}

        {can('devices', 'view') && (
        <NavLink to="/settings/devices" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/>
          </svg>
          Devices
        </NavLink>
        )}

        {user?.role === 'super_admin' && (
        <NavLink to="/super/agent-whatsapp" activeClassName="active">
          <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
          </svg>
          Agent WhatsApp
        </NavLink>
        )}

        {can('assignment', 'view') && (
          <>
            <span className="nav-section-label" style={{ marginTop: 8 }}>Assignment</span>
            <NavLink to="/admin/assignment" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                <path d="M21 21v-2a4 4 0 0 0-3-3.87"/>
              </svg>
              Assignment
            </NavLink>
          </>
        )}

        {(user?.role === 'tenant_admin' || user?.role === 'super_admin') && (
          <>
            <span className="nav-section-label" style={{ marginTop: 8 }}>Admin</span>
            <NavLink to="/admin/users" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              Users
            </NavLink>
            <NavLink to="/admin/permissions" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Permissions
            </NavLink>
            <NavLink to="/admin/integrations/indiamart" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
              IndiaMART
            </NavLink>
          </>
        )}

        {user?.role === 'super_admin' && (
          <>
            <span className="nav-section-label" style={{ marginTop: 8 }}>Platform</span>
            <NavLink to="/super/tenants" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2"/>
                <path d="M8 21h8M12 17v4"/>
              </svg>
              Tenants
            </NavLink>
            <NavLink to="/super/analyzer" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 19V6l12-3v13"/>
                <circle cx="6" cy="18" r="3"/>
                <circle cx="18" cy="16" r="3"/>
              </svg>
              Analyzer
            </NavLink>
            <NavLink to="/super/ab-review" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3v18"/>
                <path d="M16 3v18"/>
                <path d="M3 8h5"/>
                <path d="M16 8h5"/>
                <path d="M3 16h5"/>
                <path d="M16 16h5"/>
              </svg>
              A/B Review
            </NavLink>
            <NavLink to="/super/tenant-email" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
              </svg>
              Tenant Toggles
            </NavLink>
            <NavLink to="/super/ai-usage" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
              </svg>
              AI Usage
            </NavLink>
            <NavLink to="/super/teleobi" activeClassName="active">
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Teleobi
            </NavLink>
          </>
        )}
      </nav>

      {/* User info at bottom */}
      <div className="sidebar-user">
        <div className="sidebar-user-avatar">{user?.name.slice(0, 2).toUpperCase()}</div>
        <div className="sidebar-user-info">
          <div className="sidebar-user-name">{user?.name}</div>
          <div className="sidebar-user-role">{{ super_admin: 'Super Admin', tenant_admin: 'Admin', manager: 'Manager', marketing: 'Marketing', agent: 'Agent', external_user: 'External User' }[user?.role ?? 'agent']}</div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
